import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { fetchMessageTemplates } from '../../api/messageTemplates';
import { validateName } from '../../api/validateName';
import { validateReciever } from '../../api/validateReceiver';
import { paymentCall } from '../../api/paymentCall';
import { formatStr } from '../../utils/formatStr';
import { formatPersianNumber } from '../../utils/formatPersianNumber';
import PublicHeader from '../../components/ui/PublicHeader';
import PublicFooter from '../../components/ui/PublicFooter';
import Icon from '../../components/AppIcon';
import TemplateCard from './components/TemplateCard';
import RecipientForm from './components/RecipientForm';
import RulesSection from './components/RulesSection';
import Toast from '../../components/ui/Toast';
import { useToast } from '../../hooks/useToast';

const MessageSendingInterface = () => {
  const { toast, showToast, hideToast } = useToast(3000);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedName, setSelectedName] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [agreedToRules, setAgreedToRules] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [messageTemplates, setMessageTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isValidatingName, setIsValidatingName] = useState(false);
  const [nameError, setNameError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  useEffect(() => {
    fetchMessageTemplates()
      .then((templates) => {
        setMessageTemplates(templates);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || 'Error loading templates');
        setLoading(false);
      });
  }, []);

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setCurrentStep(2);
  };

  const handlePhoneSubmit = async (phone) => {
    try {
      const isValid = await validateReciever(phone);
      if (!isValid) {
        alert('شماره گیرنده معتبر نیست. لطفا شماره دیگری وارد کنید.');
        return;
      }
      setRecipientPhone(phone);
      setCurrentStep(4);
    } catch (error) {
      alert('خطا در اعتبارسنجی شماره گیرنده، لطفا دوباره تلاش کنید.');
    }
  };

  const handlePayment = async () => {
    if (!agreedToRules) {
      alert('لطفاً قوانین را مطالعه کرده و تیک تایید را بزنید');
      return;
    }

    setIsProcessing(true);
    try {
      const redirectUrl = await paymentCall(
        recipientPhone,
        selectedName,
        selectedTemplate.id,
      );
      if (redirectUrl) window.location.assign(redirectUrl);
    } catch (error) {
      showToast(`هدایت به درگاه دچار مشکل است. ${error}`, 'error');
      console.error('Payment callback failed:', error);
    }
    setIsProcessing(false);
  };

  const finalMessage =
    selectedTemplate && selectedName
      ? formatStr(selectedTemplate?.value, selectedName)
      : '';

  const stepLabels = ['انتخاب قالب', 'انتخاب نام', 'شماره گیرنده', 'پرداخت'];

  return (
    <>
      <Helmet>
        <title>ارسال پیام ناشناس - Khejalatiam</title>
        <meta
          name="description"
          content="پیام ناشناس خود را انتخاب کرده، نام گیرنده را وارد کنید و به راحتی ارسال کنید"
        />
      </Helmet>

      <Toast
        visible={toast.visible}
        message={toast.message}
        type={toast.type}
        onClose={hideToast}
      />

      <div
        className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50"
        dir="rtl"
      >
        <PublicHeader />

        <main className="container mx-auto px-4 py-8 max-w-4xl">
          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-center gap-2">
              {[1, 2, 3, 4].map((step) => (
                <React.Fragment key={step}>
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-full font-bold transition-all ${currentStep >= step
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                        : 'bg-gray-200 text-gray-400'
                      }`}
                  >
                    {formatPersianNumber(step)}
                  </div>
                  {step < 4 && (
                    <div
                      className={`h-1 w-12 rounded transition-all ${currentStep > step
                          ? 'bg-gradient-to-r from-purple-600 to-pink-600'
                          : 'bg-gray-200'
                        }`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
            <div className="flex justify-center gap-8 mt-3 text-sm">
              {stepLabels.map((label, index) => (
                <span
                  key={label}
                  className={
                    currentStep >= index + 1
                      ? 'text-purple-600 font-semibold'
                      : 'text-gray-400'
                  }
                >
                  {label}
                </span>
              ))}
            </div>
          </div>

          {/* Step 1: Template Selection */}
          {currentStep === 1 && (
            <div className="space-y-6">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-20 space-y-6">
                  <div className="h-12 w-12 rounded-full border-4 border-t-transparent animate-spin border-purple-500" />
                  <p className="text-lg text-gray-600">
                    در حال دریافت قالب‌های پیام...
                  </p>
                </div>
              ) : error ? (
                <div className="bg-white rounded-2xl shadow-xl p-8 text-center space-y-4">
                  <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-red-100">
                    <Icon name="AlertCircle" size={32} color="#dc2626" />
                  </div>
                  <p className="text-lg text-gray-700">{error}</p>
                  <button
                    type="button"
                    onClick={() => window.location.reload()}
                    className="inline-flex items-center justify-center gap-2 px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold transition-all"
                  >
                    تلاش مجدد
                  </button>
                </div>
              ) : (
                <>
                  <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-gray-800 mb-2">
                      یک قالب پیام انتخاب کنید
                    </h1>
                    <p className="text-gray-600">
                      پیام خود را به صورت ناشناس ارسال کنید
                    </p>
                  </div>
                  <div className="grid md:grid-cols-3 gap-6">
                    {messageTemplates?.map((template) => (
                      <TemplateCard
                        key={template?.id}
                        template={template}
                        onSelect={handleTemplateSelect}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 2: Name Input */}
          {currentStep === 2 && (
            <div className="max-w-2xl mx-auto">
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mb-4">
                    <span className="text-3xl">✍️</span>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">
                    نام گیرنده
                  </h2>
                </div>

                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!selectedName?.trim()) return;
                    setNameError('');
                    setIsValidatingName(true);
                    try {
                      const isValid = await validateName(selectedName.trim());
                      if (!isValid) {
                        setNameError(
                          'نام وارد شده معتبر نیست. لطفا نام دیگری وارد کنید.',
                        );
                      } else {
                        setCurrentStep(3);
                      }
                    } catch (error) {
                      setNameError(
                        'خطایی در اعتبارسنجی نام رخ داده است. لطفا دوباره تلاش کنید.',
                      );
                    } finally {
                      setIsValidatingName(false);
                    }
                  }}
                  className="space-y-6"
                >
                  <div>
                    <input
                      type="text"
                      value={selectedName}
                      onChange={(e) => {
                        setSelectedName(e?.target?.value);
                        if (nameError) setNameError('');
                      }}
                      placeholder="مثال: مریم، پدرام،‌ آتنا، مهرداد، ..."
                      className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none text-center text-lg font-semibold transition-all ${nameError
                          ? 'border-red-400 focus:border-red-500'
                          : 'border-purple-200 focus:border-purple-500'
                        }`}
                      autoFocus
                      required
                    />
                    {nameError && (
                      <p className="mt-2 text-sm text-red-600 flex items-center justify-center gap-1">
                        <span>⚠️</span>
                        {nameError}
                      </p>
                    )}
                    {!nameError && (
                      <p className="mt-2 text-xs text-gray-500 text-center">
                        هر نامی که می‌خواهید می‌توانید وارد کنید
                      </p>
                    )}
                  </div>

                  {selectedName?.trim() && (
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-6">
                      <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                        <span>👁️</span>
                        پیش‌نمایش پیام:
                      </h3>
                      <div className="bg-white rounded-lg p-4 text-right whitespace-pre-line text-sm text-gray-700 leading-relaxed">
                        {formatStr(selectedTemplate?.value, selectedName)}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(1)}
                      className="flex-1 px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl font-semibold transition-all"
                    >
                      بازگشت
                    </button>
                    <button
                      type="submit"
                      disabled={isValidatingName || !selectedName?.trim()}
                      className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed text-white rounded-xl font-semibold transition-all"
                    >
                      ادامه
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Step 3: Recipient Form */}
          {currentStep === 3 && (
            <div className="max-w-2xl mx-auto">
              <RecipientForm
                onSubmit={handlePhoneSubmit}
                onBack={() => setCurrentStep(2)}
                selectedName={selectedName}
              />
            </div>
          )}

          {/* Step 4: Preview & Payment */}
          {currentStep === 4 && (
            <div className="space-y-6 md:space-y-8 max-w-2xl mx-auto">
              {/* Order Summary Card */}
              <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 space-y-4 md:space-y-6">
                <div className="flex items-center gap-3 pb-4 border-b border-purple-100">
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl p-2">
                    <Icon name="ClipboardList" size={20} color="#ffffff" />
                  </div>
                  <h2 className="text-lg md:text-xl font-bold text-gray-800">
                    خلاصه سفارش
                  </h2>
                </div>

                {/* Message Content */}
                <div className="flex items-start gap-3 md:gap-4">
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl p-2 flex-shrink-0">
                    <Icon name="MessageSquare" size={20} color="#ffffff" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm text-gray-500 mb-1 md:mb-2">
                      متن پیام
                    </h3>
                    <p className="text-base md:text-lg font-semibold text-gray-800 whitespace-pre-line leading-relaxed">
                      {finalMessage || '---'}
                    </p>
                  </div>
                </div>

                {/* Receiver Name */}
                <div className="flex items-start gap-3 md:gap-4">
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl p-2 flex-shrink-0">
                    <Icon name="User" size={20} color="#ffffff" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm text-gray-500 mb-1 md:mb-2">
                      نام گیرنده
                    </h3>
                    <p className="text-base md:text-lg font-semibold text-gray-800">
                      {selectedName || '---'}
                    </p>
                  </div>
                </div>

                {/* Phone Number */}
                <div className="flex items-start gap-3 md:gap-4">
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl p-2 flex-shrink-0">
                    <Icon name="Phone" size={20} color="#ffffff" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm text-gray-500 mb-1 md:mb-2">
                      شماره گیرنده
                    </h3>
                    <p className="text-base md:text-lg font-semibold text-gray-800">
                      {formatPersianNumber(recipientPhone) || '---'}
                    </p>
                  </div>
                </div>

                {/* Template Info */}
                {selectedTemplate && (
                  <div className="flex items-start gap-3 md:gap-4">
                    <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl p-2 flex-shrink-0">
                      <Icon name="Layout" size={20} color="#ffffff" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm text-gray-500 mb-1 md:mb-2">
                        قالب پیام
                      </h3>
                      <p className="text-base md:text-lg font-semibold text-gray-800">
                        {selectedTemplate.name}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Rules Section */}
              <RulesSection
                accepted={agreedToRules}
                onAcceptChange={setAgreedToRules}
                error=""
              />

              {/* Payment Card */}
              <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 space-y-4 md:space-y-6">
                <div className="flex items-center gap-3 pb-4 border-b border-purple-100">
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl p-2">
                    <Icon name="CreditCard" size={20} color="#ffffff" />
                  </div>
                  <h2 className="text-lg md:text-xl font-bold text-gray-800">
                    پرداخت
                  </h2>
                </div>

                {/* Amount */}
                <div className="flex justify-between items-center p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl">
                  <span className="text-sm md:text-base text-gray-500">
                    مبلغ قابل پرداخت
                  </span>
                  <span className="text-xl md:text-2xl lg:text-3xl font-bold text-purple-600">
                    {formatPersianNumber(selectedTemplate?.price)} تومان
                  </span>
                </div>

                {/* Pay Button */}
                <button
                  type="button"
                  disabled={!agreedToRules || isProcessing}
                  onClick={handlePayment}
                  className="inline-flex items-center justify-center gap-2 w-full px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed text-white rounded-xl font-semibold text-lg transition-all"
                >
                  {isProcessing ? (
                    <>
                      <div className="h-5 w-5 rounded-full border-2 border-t-transparent animate-spin border-white" />
                      در حال انتقال به درگاه...
                    </>
                  ) : (
                    <>
                      <Icon name="CreditCard" size={20} color="#ffffff" />
                      پرداخت و ارسال پیام
                    </>
                  )}
                </button>

                {/* Security Badges */}
                <div className="pt-4 border-t border-gray-100">
                  <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-xs md:text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                      <Icon name="Shield" size={16} color="#16a34a" />
                      <span>پرداخت امن</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="Lock" size={16} color="#16a34a" />
                      <span>رمزنگاری SSL</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="CheckCircle" size={16} color="#16a34a" />
                      <span>تضمین ارسال</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Back Button */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setCurrentStep(3)}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl font-semibold transition-all"
                >
                  <Icon name="ArrowRight" size={18} />
                  بازگشت به مرحله قبل
                </button>
              </div>
            </div>
          )}
        </main>

        <PublicFooter />
      </div>
    </>
  );
};

export default MessageSendingInterface;
